import { useId } from 'react'
import type { InputTrack, VideoCodec } from '../api'
import { t } from '../i18n'
import { Check, Note, OptionList, Tag, type Option } from '../ui'
import { byKind, codecName, ixOf, mainOf, multitrack, rate, sendable, vertical, videoCodecOf } from './tracks'

/** The track choice shows for a multitrack input, or when the output already has a track other than 0. */
export const showTracks = (tracks: InputTrack[] | undefined, video: number, audio: number) => multitrack(tracks) || video !== 0 || audio !== 0

/** ≤ 6 choices: an option list with the reason under the unavailable ones; more: a select of the available ones. */
const MAX_OPTIONS = 6

/**
 * Which video and audio track an output sends (design system TrackSelection). Tracks the platform
 * cannot take stay visible and unavailable, the reason as text under them (no tooltips). A selected
 * track that is not in the stream now stays listed as such. A change applies at the next keyframe
 * without reconnecting (docs/ertmp.md §5.3).
 */
export function TrackChoice({ tracks, platformLabel, video, audio, codecs, extra, enhanced, disabled, onVideo, onAudio }: {
  tracks?: InputTrack[]; platformLabel: string; video: number; audio: number;
  /** the video codecs the output sends now (H.264; + the profile's others with enhanced_codecs on) */
  codecs: VideoCodec[];
  /** the profile's codecs beyond H.264 by this protocol: their tracks say which setting lets them through */
  extra: VideoCodec[];
  /** the HEVC / AV1 checkbox (E1h), when the profile has such codecs */
  enhanced?: EnhancedProps;
  disabled?: boolean;
  onVideo: (v: number) => void; onAudio: (v: number) => void;
}) {
  return (
    <div data-testid="dest-tracks" className="flex flex-col gap-3">
      <Choice kind="video" tracks={tracks} value={video} platformLabel={platformLabel} codecs={codecs} extra={extra} disabled={disabled} onChange={onVideo} />
      <Choice kind="audio" tracks={tracks} value={audio} platformLabel={platformLabel} codecs={codecs} extra={extra} disabled={disabled} onChange={onAudio} />
      {enhanced && <Enhanced {...enhanced} platformLabel={platformLabel} disabled={disabled || enhanced.disabled} />}
    </div>
  )
}

export interface EnhancedProps {
  value: boolean
  saved: boolean
  /** HLS: the platform takes H.264 only — the checkbox is off and says why */
  hls?: boolean
  /** the Beta tag: from the profile (enhanced_codecs_beta, ERTMP-CAT-1), not a constant */
  beta?: boolean
  disabled?: boolean
  onChange: (v: boolean) => void
}

/**
 * «Send HEVC and AV1 as they are» + Beta while the profile says so (design system TrackSelection «HEVC / AV1 for YouTube»): a
 * checkbox, as the setting is saved with Save; a change reconnects the output once, which an inline
 * note says while it is unsaved (no dialog: the change is reversible). With HLS it is unavailable.
 */
function Enhanced({ value, saved, hls, beta, disabled, onChange, platformLabel }: EnhancedProps & { platformLabel: string }) {
  return (
    <div className="flex flex-col gap-2" data-testid="dest-enhanced">
      {/* DS: ERTMP-CAT-01 — Beta from Profile.enhanced_codecs_beta (verified ≠ passed), not a constant */}
      <Check testId="dest-enhanced-check" checked={value && !hls} disabled={disabled || hls} onChange={onChange}
        label={<>{t('dest.hevc')}{beta && <Tag>{t('dest.beta')}</Tag>}</>}
        hint={hls ? t('dest.hevcHls', { platform: platformLabel }) : t('dest.hevcHint', { platform: platformLabel })} />
      {value !== saved && !hls && (
        <Note state="warn" data-testid="dest-enhanced-note">
          <b>{t('dest.hevcApplies')}</b> {t(value ? 'dest.hevcOnNote' : 'dest.hevcOffNote', { platform: platformLabel })}
        </Note>
      )}
    </div>
  )
}

function Choice({ kind, tracks, value, platformLabel, codecs, extra, disabled, onChange }: {
  kind: InputTrack['kind']; tracks?: InputTrack[]; value: number; platformLabel: string; codecs: VideoCodec[]; extra: VideoCodec[]; disabled?: boolean; onChange: (v: number) => void;
}) {
  // why a track cannot be chosen now: the checkbox would let it through, or the platform never takes it
  const why = (tr: InputTrack) => {
    if (sendable(tr, codecs)) return undefined
    if (kind === 'audio') return t('dest.trackAac', { platform: platformLabel })
    const c = videoCodecOf(tr.codec)
    return c && extra.includes(c) ? t('dest.trackEnhanced', { platform: platformLabel, codec: codecName(tr.codec) }) : t('dest.trackH264', { platform: platformLabel })
  }
  const id = useId()
  const label = t(kind === 'video' ? 'dest.videoTrack' : 'dest.audioTrack')
  const list = byKind(tracks, kind).filter((x) => x.state === 'live')
  const options: Option<number>[] = list.map((tr) => ({
    value: tr.index,
    ix: ixOf(kind, tr.index),
    label: mainOf(tr),
    tags: vertical(tr) ? <Tag>9:16</Tag> : undefined,
    right: kind === 'video' ? codecName(tr.codec) : tr.kbps ? rate(tr.kbps) : undefined,
    why: why(tr),
    testId: `dest-track-${ixOf(kind, tr.index)}`,
  }))
  // track 0 always exists as a choice (the default); the saved one stays listed while it is away
  for (const i of [0, value]) {
    if (!options.some((o) => o.value === i)) options.push({ value: i, ix: ixOf(kind, i), label: t('dest.trackGone'), testId: `dest-track-${ixOf(kind, i)}` })
  }
  options.sort((a, b) => a.value - b.value)
  if (options.length <= 1) return null
  if (options.length <= MAX_OPTIONS) {
    return (
      <div className="sx-field" data-testid={`dest-${kind}-track`}>
        <span className="lbl" id={`${id}-l`}>{label}</span>
        <OptionList labelledBy={`${id}-l`} value={value} options={options} disabled={disabled} onChange={onChange} />
      </div>
    )
  }
  // more than 6: a select of what the platform takes, one line on what is left out
  const ok = options.filter((o) => o.why === undefined)
  const hidden = options.length - ok.length
  return (
    <div className="sx-field" data-testid={`dest-${kind}-track`}>
      <label htmlFor={`${id}-s`}>{label}</label>
      <select id={`${id}-s`} className="sx-select font-mono text-xs" value={value} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))}
        aria-describedby={hidden ? `${id}-h` : undefined}>
        {ok.map((o) => <option key={o.value} value={o.value}>{`${o.ix} · ${typeof o.label === 'string' ? o.label : ''}`}</option>)}
      </select>
      {hidden > 0 && <span className="help" id={`${id}-h`}>{t('dest.tracksHidden', { platform: platformLabel })}</span>}
    </div>
  )
}
